import type { MaskAdapter } from "../types";

export abstract class BaseMaskAdapter implements MaskAdapter {
  maskPattern: string;

  abstract formatForDisplay(value: unknown): string;
  abstract parseFromInput(inputValue: string): { value: string | number; formatted: string };
  abstract handleControlledUpdate(value: unknown): string;

  constructor(maskPattern: string) {
    this.maskPattern = maskPattern;
  }

  handleEmptyValue(): string {
    return "";
  }

  isEmpty(value: unknown): boolean {
    return value === "" || value === null || value === undefined || value === 0;
  }

  /**
   * Utility method to apply generic mask pattern
   */
  protected applyGenericMask(value: string): string {
    if (!this.maskPattern) return value;

    const cleanValue = this.cleanValue(value);

    let maskedResult = "";
    let valueIndex = 0;
    let maskIndex = 0;

    while (maskIndex < this.maskPattern.length && valueIndex < cleanValue.length) {
      const maskChar = this.maskPattern[maskIndex];
      const nextMaskChar = this.maskPattern[maskIndex + 1];

      if (maskChar === "#" || maskChar === "9") {
        if (nextMaskChar === "?") {
          const remainingDigits = cleanValue.length - valueIndex;
          const remainingMask = this.maskPattern.slice(maskIndex + 2);
          const requiredDigits = (remainingMask.match(/[#9]/g) || []).length;

          if (remainingDigits > requiredDigits) {
            maskedResult += cleanValue[valueIndex];
            valueIndex++;
          }
          maskIndex += 2;
        } else {
          maskedResult += cleanValue[valueIndex];
          valueIndex++;
          maskIndex++;
        }
      } else {
        maskedResult += maskChar;
        maskIndex++;
      }
    }

    return maskedResult;
  }

  /**
   * Utility method to clean numeric input (remove everything except digits)
   */
  protected cleanNumericInput(input: string): string {
    return input.replace(/\D/g, "");
  }

  protected cleanValue(value: string): string {
    if (this.maskPattern?.includes("#")) {
      return value.replace(/[^a-zA-Z0-9]/g, "");
    }

    return value.replace(/\D/g, "");
  }

  /**
   * Checks if a key is a control key (navigation, deletion, etc.)
   */
  protected isControlKey(key: string): boolean {
    const controlKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    return controlKeys.includes(key);
  }

  /**
   * Default implementation - allows all keys
   * Override in subclasses to restrict input
   */
  isKeyAllowed(_key: string, _currentValue: string, _selectionStart: number | null): boolean {
    return true;
  }
}
